import * as React from "react"
import { getImage } from "gatsby-plugin-image"
import { convertToBgImage } from "gbimage-bridge"
import BackgroundImage from "gatsby-background-image"
import { Box, Flex, Text, Grid, Heading, css } from "@chakra-ui/react"
import { Container } from "../layout/Container"
import { colors } from "../colors/colors"
import { Image } from "../image/Image"
import { CTA } from "./CTA"
import { Header, Headline } from "./Headline"

export function Content({
  direction = `left`,
  images,
  graphic,
  sectionTitle,
  subtitle,
  subtitleColor,
  citation,
  backgroundImage,
  backgroundColor,
  description,
  descriptionSize = 20,
  descriptionColor,
  size,
  title,
  titleColor,
  cta,
  ctaLink,
  ctaColor,
  ctaArray,
  video,
}) {
  const large = size === `large`

  const videoUrl = video?.mediaItemUrl

  const hasImages = !videoUrl && images && images.length > 0
  const hasMoreThanOneImage = !videoUrl && images && images.length > 1

  let flexDirection
  let gridColumn
  let justifyImage
  let gridRow = `1`

  if (direction === `center`) {
    gridColumn = `span 12`
    flexDirection = `column-reverse`
    justifyImage = "center"
    gridRow = `2`
  } else if (direction === `left`) {
    flexDirection = `row`
    gridColumn = "2 / 7"
    justifyImage = large ? `flex-end` : `flex-start`
  } else {
    flexDirection = `row-reverse`
    gridColumn = "7 / 11"
    justifyImage = `flex-start`
  }

  let mr
  let ml
  let pt = { base: `30px`, md: `30px` }

  let pb = { base: `30px`, md: `30px` }

  let leftColumn
  let rightColumn
  let textBoxPadding = {}
  let width = {
    base: `100%`,
    md: `100%`,
  }
  let imageJustification = `flex-start`
  let imageBasis = `100%`

  if (hasMoreThanOneImage) {
    imageBasis = `75%`
    pb = `146px`
  }

  if (direction === `center`) {
    mr = `auto`
    ml = `auto`
    pt = { base: `30px`, md: `100px` }
    width = {
      base: `100%`,
      md: `768px`,
    }
    leftColumn = `span 4`
    rightColumn = `7 / span 6`
  } else if (direction === `left`) {
    mr = `0`
    ml = `auto`
    leftColumn = { base: `span 5`, md: `span 4`, lg: `span 6` }
    rightColumn = { base: `span 4`, md: `5 / span 4`, lg: `7 / span 6` }

    if (size === `hero`) {
      leftColumn = { base: `span 5`, lg: `span 6`, xl: `span 7` }
      rightColumn = { base: `6 / span 4`, lg: `8 / span 4` }
    }

    imageJustification = `flex-end`

    if (size === `small`) {
      textBoxPadding = { p: { base: 0, lg: "18px 0" } }
    }
  } else if (direction === `right`) {
    mr = `auto`
    ml = `0`
    leftColumn = { base: `span 4`, md: `5 / span 4`, lg: `7 / span 6` }
    rightColumn = { base: `span 4`, md: `1 / span 4`, lg: `1 / span 6` }
  }

  if (direction === `center`) {
    leftColumn = { base: `span 4`, md: `span 8`, lg: `span 12` }
    gridRow = `2`
    rightColumn = { base: `span 4`, md: `span 8`, lg: `span 12` }
    imageJustification = "center"
  }

  const graphicImg = graphic ? (
    <Flex justifyContent="flex-start" display={{ base: `none`, lg: `flex` }}>
      <Box flexBasis="200px" bg={backgroundColor}>
        <Image image={graphic?.localFile} />
      </Box>
    </Flex>
  ) : null

  let imgUrl

  if (large) {
    imgUrl = images[0]?.localFile?.childImageSharp?.normal
  } else if (size === `hero`) {
    imgUrl = images[0]?.localFile?.childImageSharp?.normal
  } else {
    imgUrl = images[0]?.localFile?.childImageSharp?.small
  }

  if (size === `small`) {
    imageBasis = `auto`
    imageJustification = "center"
  }

  const videoSection = videoUrl ? (
    <video autoPlay muted loop style={{ width: `100%` }}>
      <source src={videoUrl} type="video/mp4" />
    </video>
  ) : null

  const featuredImage = hasImages ? (
    <Flex
      justifyContent={imageJustification}
      width="100%"
      style={{
        margin: direction === `center` ? `0 0 35px` : `0`,
      }}
    >
      <Box
        flexBasis={direction === `center` ? 180 : imageBasis}
        bg={backgroundColor}
      >
        {imgUrl ? (
          <Image image={imgUrl} />
        ) : (
          <img src={images[0]?.localFile?.publicURL} />
        )}
      </Box>
    </Flex>
  ) : null

  const secondaryImage = hasMoreThanOneImage ? (
    <Box textAlign={direction === `left` ? `right` : `left`}>
      {images.slice(1).map((file, index) => {
        return (
          <Box
            key={`file_${index}`}
            position="absolute"
            width="calc(42.44%)"
            left="75%"
            height="auto"
            bottom={0}
            transform="translate3d(-60%,50%,0)"
          >
            <Image image={file?.localFile?.childImageSharp?.normal} />
          </Box>
        )
      })}
    </Box>
  ) : null

  let ctaSection
  if (cta && ctaLink) {
    if (direction === `center`) {
      ctaSection = (
        <Flex justifyContent="center" mt="24px">
          <Box alignSelf="center" flexBasis="180px">
            <Box pl="24px">
              <CTA
                internalLink={ctaLink?.startsWith("/")}
                direction={direction}
                large={large}
                color={ctaColor || colors.purple}
                cta={cta}
                ctaLink={ctaLink}
              />
            </Box>
          </Box>
        </Flex>
      )
    } else {
      ctaSection = (
        <CTA
          internalLink={ctaLink.startsWith("/")}
          direction={direction}
          large={large}
          color={ctaColor || colors.purple}
          cta={cta}
          ctaLink={ctaLink}
        />
      )
    }
  }

  const inner = (
    <Container
      pt={pt}
      pb={pb}
      mb={0}
      pr={size === `small` ? { base: `20px`, xl: 0 } : undefined}
    >
      {sectionTitle && <Header textAlign="center">{sectionTitle}</Header>}
      <Grid
        gap={2}
        gridAutoFlow="dense"
        templateColumns={{
          base: `1fr`,
          sm: `1fr`,
          md: `repeat(8, 1fr)`,
          lg: `repeat(12, 1fr)`,
        }}
      >
        <Box
          gridRow={gridRow}
          alignSelf="center"
          gridColumn={leftColumn}
          textAlign={direction === `center` ? `center` : `left`}
        >
          <Box {...textBoxPadding}>
            {graphicImg}

            {title && (
              <Heading
                as="h2"
                fontSize="24px"
                pb="1.2rem"
                m={0}
                fontWeight={500}
                color={titleColor}
              >
                <div dangerouslySetInnerHTML={{ __html: title }} />
              </Heading>
            )}

            {subtitle && (
              <Heading
                as="h3"
                fontSize={{
                  base: "36px",
                  xl: `50px`,
                }}
                letterSpacing={large ? "-1px" : "normal"}
                fontWeight={500}
                lineHeight="0.84"
                m={0}
                pb="1.2rem"
                textTransform={direction !== `center` ? "uppercase" : `initial`}
                color={subtitleColor}
                textAlign={
                  direction !== `center` && { base: `center`, md: `initial` }
                }
              >
                <div dangerouslySetInnerHTML={{ __html: subtitle }} />
              </Heading>
            )}

            {direction !== `center` && (
              <Box
                position="relative"
                display={{
                  base: `block`,
                  md: `none`,
                }}
                mb={hasMoreThanOneImage ? "5.2rem" : `1.2rem`}
              >
                {videoSection}
                {featuredImage}
                {secondaryImage}
              </Box>
            )}
            {description && (
              <Box
                maxWidth={direction === `center` ? "535px" : `100%`}
                margin="0 auto"
              >
                <Text
                  as="p"
                  fontSize={descriptionSize || 20}
                  lineHeight="28px"
                  fontWeight={400}
                  pb="1.2rem"
                  color={descriptionColor}
                >
                  <div dangerouslySetInnerHTML={{ __html: description }} />
                </Text>
              </Box>
            )}

            {citation && (
              <Text m="0px" mb="20px" fontStyle="italic" color="#8E8E93">
                {citation}
              </Text>
            )}

            {ctaSection}

            {ctaArray && ctaArray.length > 0 && (
              <Flex>
                {ctaArray.map(({ ctaLabel, ctaColor, ...rest }) => {
                  return (
                    <Box flex="1 0 0px">
                      <CTA
                        large={large}
                        direction={direction}
                        cta={ctaLabel}
                        color={ctaColor}
                        fontSize="20px"
                        {...rest}
                      />
                    </Box>
                  )
                })}
              </Flex>
            )}
          </Box>
        </Box>
        {(hasImages || videoUrl) && (
          <Box
            alignSelf="center"
            position="relative"
            gridColumn={rightColumn}
            display={{
              base: direction !== `center` ? `none` : `block`,
              md: `block`,
            }}
          >
            {videoSection}
            {featuredImage}
            {secondaryImage}
          </Box>
        )}
      </Grid>
    </Container>
  )

  if (backgroundColor && !backgroundImage) {
    return <Box backgroundColor={backgroundColor}>{inner}</Box>
  }

  if (backgroundImage) {
    const imgUrl = getImage(backgroundImage?.localFile?.childImageSharp?.fluid)
    const bgImage = convertToBgImage(imgUrl)
    return (
      <BackgroundImage
        {...bgImage}
        style={{
          marginTop: `48px`,
          backgroundRepeat: `no-repeat`,
          backgroundSize: `cover`,
          backgroundPosition: "50% 50%",
          height: `100%`,
          width: `100%`,
        }}
      >
        {inner}
      </BackgroundImage>
    )
  }

  return inner
}
